// Purchasing documents in a supplier's currency (docs/multi-currency-purchasing-plan.md).
//
// A purchase order, its bills and payments may be in any of the 21 purchase
// currencies, not only the tenant's 7. Format and parse those amounts here,
// never through `toCurrencyCode` / `formatCurrency(…, CurrencyCode)`: the
// former turns any code outside the tenant list into INR.

import { PURCHASE_CURRENCIES } from '@bbpos/types';
import { amountToMajor, convertMinorUnits, formatAmount, getSubunitMultiplier } from '@bbpos/utils';

/** Options for a purchase-currency select: "INR — Indian Rupee". */
export const PURCHASE_CURRENCY_OPTIONS = PURCHASE_CURRENCIES.map((c) => ({
  value: c.code,
  label: `${c.code} — ${c.name}`,
}));

/** An amount (minor units) in a purchasing document's own currency. */
export function formatDocAmount(minor: number, currency: string): string {
  return formatAmount(minor, currency);
}

/** The symbol (or ISO code) formatDocAmount puts in front of an amount. */
export function docCurrencySymbol(currency: string): string {
  return formatAmount(0, currency).slice(0, -amountToMajor(0, currency).length);
}

/** A typed rate the API takes: positive, up to 8 decimals, at most 1,000,000. */
export function isValidExchangeRate(rate: string): boolean {
  const n = Number(rate);
  return /^\d{1,10}(\.\d{1,8})?$/.test(rate.trim()) && n > 0 && n <= 1_000_000;
}

/** An amount in the tenant currency at `rate`, or null while the rate is not valid. */
export function toTenantAmount(minor: number, from: string, to: string, rate: string): number | null {
  return isValidExchangeRate(rate) ? convertMinorUnits(minor, from, to, rate.trim()) : null;
}

/** Smallest step an amount input in `currency` takes: 0.01, 0.001, or 1 for JPY. */
export function currencyStep(currency: string): number {
  return 1 / getSubunitMultiplier(currency);
}
